// **React Imports
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

// **MUI Imports
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  Typography
} from '@mui/material'

// **Component Imports
import IconifyIcon from '../icon'
import CustomTextField from '../textfield'
import ChatHistory from './ChatHistory'
import ChatMessage from './ChatMessage'
import ResendCandidateAssessmentsModal from './ResendCandidateAssessmentsModal'
import EnableOrScheduleStageModal from '../../pages/contest/edit/assessment-dashboard/components/EnableOrScheduleStageModal'

// **Api Imports
import {
  getAllThreads,
  getThreadMessages,
  getThreadMessageStatus,
  sendThreadMessage
} from '../../axios/api/chatbot'

// **Styles Imports
import './index.scss'

import { useNavigate, useParams } from 'react-router-dom'
import scoutyImg from '../../assets/images/scouty.svg'
import ReEnableAttemptModal from '../../pages/contest/edit/assessment-dashboard/components/ReEnableAttemptModal'
import InviteCandidate from '../../pages/contest/edit/candidates/InviteCandidate'
import { CHALLENGE_TYPES, CHAT_BOT_ACTION_TYPES } from '../../utils/constant'
import { showErrorToast } from '../../utils/utils'
import { useSelector } from 'react-redux'

const FAB_SIZE = 56
const FAB_DEFAULT_OFFSET = 28
const DRAG_THRESHOLD = 3
const CHAT_WINDOW_WIDTH = 420
const CHAT_WINDOW_MAX_HEIGHT = 600
const CHAT_WINDOW_TOP_GAP = 140
const CHAT_WINDOW_EDGE_GAP = 16
const CHAT_WINDOW_FAB_GAP = 68

const getBoundedFabPosition = (right, bottom) => ({
  right: Math.max(0, Math.min(window.innerWidth - FAB_SIZE, right)),
  bottom: Math.max(0, Math.min(window.innerHeight - FAB_SIZE, bottom))
})

const Chatbot = () => {
  // Hooks
  const navigate = useNavigate()
  const { contestId: paramsContestId } = useParams()

  // Refs
  const reenableDataRef = useRef(null)
  const inputRef = useRef(null)
  const messagesEndRef = useRef(null)
  const pollIntervalIdRef = useRef(null)
  const isUserScrolledUp = useRef(false)
  const rafRef = useRef(null)
  const contestStagesRef = useRef([])
  const pendingActionRef = useRef(null)
  const dragRef = useRef({
    isDragging: false,
    startX: 0,
    startY: 0,
    initRight: 0,
    initBottom: 0,
    hasMoved: false
  })

  //Redux State
  const contestStages = useSelector((state) => state?.contest?.chatbotStages)

  // State
  const [isOpen, setIsOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [maxTokens, setMaxTokens] = useState(0)
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [fetchMessages, setFetchMessages] = useState(false)
  const [chatHistory, setChatHistory] = useState([])
  const [activeChatId, setActiveChatId] = useState(null)
  const [cumulativeTokens, setCumulativeTokens] = useState(0)
  const [isFetchingHistory, setIsFetchingHistory] = useState(false)
  const [isBotTyping, setIsBotTyping] = useState(false)
  const [modalData, setModalData] = useState({ open: false, data: null })
  const [selectAssessmentModal, setSelectAssessmentModal] = useState({
    open: false,
    candidate: null,
    message: null
  })
  const [openReEnableCandidateModal, setOpenReEnableCandidateModal] = useState({
    open: false,
    data: null
  })
  const [openInterviewScheduleModal, setOpenInterviewScheduleModal] = useState({
    open: false,
    candidateName: null,
    contestId: null,
    actionType: null,
    inputData: null
  })

  const [fabPos, setFabPos] = useState({
    right: FAB_DEFAULT_OFFSET,
    bottom: FAB_DEFAULT_OFFSET
  })

  const fabPosRef = useRef(fabPos)

  const [isDragging, setIsDragging] = useState(false)
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  })

  // Variables
  const showHistory = useMemo(
    () => !activeChatId && messages?.length === 0,
    [messages?.length, activeChatId]
  )

  const lastMessage = useMemo(
    () =>
      activeChatId && messages?.length > 0 ? messages?.slice(-1)?.[0] : null,
    [messages, activeChatId]
  )

  const disableTyping = useMemo(
    () =>
      isLoading ||
      fetchMessages ||
      isBotTyping ||
      lastMessage?.status === 'processing' ||
      lastMessage?.status === 'awaiting_approval' ||
      cumulativeTokens > maxTokens,
    [
      isLoading,
      lastMessage,
      fetchMessages,
      cumulativeTokens,
      maxTokens,
      isBotTyping
    ]
  )

  const chatWindowHeight = useMemo(
    () =>
      Math.min(
        windowDimensions.height - CHAT_WINDOW_TOP_GAP,
        CHAT_WINDOW_MAX_HEIGHT
      ),
    [windowDimensions?.height]
  )

  const chatWindowRight = useMemo(
    () =>
      Math.max(
        CHAT_WINDOW_EDGE_GAP,
        Math.min(
          fabPos.right,
          windowDimensions.width - CHAT_WINDOW_WIDTH - CHAT_WINDOW_EDGE_GAP
        )
      ),
    [fabPos?.right, windowDimensions?.width]
  )

  const chatWindowBottom = useMemo(
    () =>
      Math.max(
        CHAT_WINDOW_EDGE_GAP,
        Math.min(
          fabPos.bottom + CHAT_WINDOW_FAB_GAP,
          windowDimensions.height - chatWindowHeight - CHAT_WINDOW_EDGE_GAP
        )
      ),
    [windowDimensions?.height, fabPos?.bottom, chatWindowHeight]
  )

  // Handlers
  const fetchThreads = useCallback(async () => {
    setIsFetchingHistory(true)
    const res = await getAllThreads()
    setIsFetchingHistory(false)

    if (res?.isError) {
      showErrorToast(res)
    } else {
      setChatHistory(res?.threads || [])
    }
  }, [])

  const toggleChat = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) {
        setActiveChatId(null)
      }

      return !prev
    })
  }, [])

  const updateFabPosition = useCallback((right, bottom) => {
    const nextPosition = getBoundedFabPosition(right, bottom)
    fabPosRef.current = nextPosition

    if (rafRef.current) return

    rafRef.current = requestAnimationFrame(() => {
      setFabPos(fabPosRef.current)
      rafRef.current = null
    })
  }, [])

  const handlePointerDown = useCallback((e) => {
    if (e.button !== undefined && e.button !== 0) return

    try {
      e.currentTarget.setPointerCapture(e.pointerId)
    } catch (err) {
      // Ignore pointer capture errors
    }

    dragRef.current = {
      isDragging: true,
      hasMoved: false,
      startX: e.clientX,
      startY: e.clientY,
      initRight: fabPosRef.current.right,
      initBottom: fabPosRef.current.bottom
    }

    setIsDragging(true)
  }, [])

  const handlePointerMove = useCallback(
    (e) => {
      if (!dragRef.current.isDragging) return

      const dx = e.clientX - dragRef.current.startX
      const dy = e.clientY - dragRef.current.startY

      if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
        dragRef.current.hasMoved = true
      }

      if (!dragRef.current.hasMoved) return

      updateFabPosition(
        dragRef.current.initRight - dx,
        dragRef.current.initBottom - dy
      )
    },
    [updateFabPosition]
  )

  const handlePointerUp = useCallback((e) => {
    if (!dragRef.current.isDragging) return

    dragRef.current.isDragging = false
    setIsDragging(false)

    try {
      if (e.currentTarget.hasPointerCapture(e.pointerId)) {
        e.currentTarget.releasePointerCapture(e.pointerId)
      }
    } catch (err) {
      // Ignore pointer release errors
    }
  }, [])

  const handleFabClick = useCallback(
    (e) => {
      if (dragRef.current.hasMoved) {
        e.preventDefault()
        e.stopPropagation()
        return
      }

      toggleChat()
    },
    [toggleChat]
  )

  const handleNewChat = useCallback(() => {
    setActiveChatId(null)
    setTimeout(() => inputRef.current?.focus(), 100)
  }, [])

  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target
    isUserScrolledUp.current = scrollHeight - scrollTop - clientHeight > 50
  }, [])

  const scrollToLatestMessage = useCallback(
    (behavior = 'smooth', force = false) => {
      if (force || !isUserScrolledUp.current) {
        messagesEndRef.current?.scrollIntoView({ behavior })
      }
    },
    []
  )

  const handleSelectChat = useCallback(async (chat) => {
    setActiveChatId(chat.threadId || chat.id)
    setFetchMessages(true)

    const res = await getThreadMessages(chat.threadId || chat.id)

    setFetchMessages(false)

    if (res?.isError) {
      showErrorToast(res)
      setMessages([])
      setMaxTokens(0)
      setCumulativeTokens(0)
    } else {
      setMessages(res?.messages || [])
      setMaxTokens(res?.maxTokens || 0)
      setCumulativeTokens(res?.cumulativeTokens || 0)
    }
  }, [])

  const onAwaitingAprrovalClick = useCallback((message) => {
    setModalData({ open: true, data: message })
  }, [])

  const onSelectCandidateClick = useCallback((message, candidate) => {
    setSelectAssessmentModal({ open: true, candidate, message })
  }, [])

  const pollStatus = useCallback(
    (messageId) => {
      clearInterval(pollIntervalIdRef.current)

      pollIntervalIdRef.current = setInterval(async () => {
        const res = await getThreadMessageStatus(messageId)

        if (res?.isError) {
          clearInterval(pollIntervalIdRef.current)
          return
        }

        const { messageId: responseMessageId, ...rest } = res

        if (rest?.status === 'processing') return

        clearInterval(pollIntervalIdRef.current)

        const messageObj = {
          id: responseMessageId,
          ...rest,
          ...(rest?.actionType === CHAT_BOT_ACTION_TYPES.CANDIDATE_SEARCH &&
          rest?.confirmation?.data?.length === 1
            ? {
                confirmation: {
                  ...rest?.confirmation,
                  navigate: true
                }
              }
            : {})
        }

        setMessages((prev) =>
          prev.map((msg) => (msg.id === responseMessageId ? messageObj : msg))
        )

        if (rest?.actionType === CHAT_BOT_ACTION_TYPES.CANDIDATE_INVITE) {
          onAwaitingAprrovalClick(messageObj)
        }

        if (
          rest?.status === 'success' &&
          [
            CHAT_BOT_ACTION_TYPES.SCHEDULE_INTERVIEW,
            CHAT_BOT_ACTION_TYPES.RE_SCHEDULE_INTERVIEW
          ].includes(pendingActionRef.current)
        ) {
          if (window?.eventEmitter) {
            window?.eventEmitter?.emit('refresh_assessment_dashboard')
          }
          pendingActionRef.current = null
        }

        if (rest?.cumulativeTokens) {
          setCumulativeTokens(rest.cumulativeTokens)
        }

        if (rest?.maxTokens) {
          setMaxTokens(rest.maxTokens)
        }
      }, 3000)
    },
    [onAwaitingAprrovalClick]
  )

  const handleSendMessage = useCallback(
    async (inputPayload) => {
      const trimmedValue = inputPayload?.message || inputValue.trim()
      if (!trimmedValue) return

      const userMessage = {
        id: `msg-${Date.now()}`,
        role: 'user',
        messageText: trimmedValue,
        status: 'success'
      }

      setMessages((prev) => [...prev, userMessage])
      setInputValue('')

      const payload = {
        message: trimmedValue,
        ...(inputPayload || {})
      }

      if (activeChatId) {
        payload.threadId = activeChatId
      }

      if (paramsContestId) {
        if (payload?.payload) {
          payload.payload.contestId =
            payload?.payload?.contestId || paramsContestId
          payload.payload.isAssessment =
            window.location.pathname.startsWith('/assessments/')

          payload.payload.stages = [...contestStagesRef?.current]
        } else {
          payload.payload = {
            contestId: paramsContestId,
            isAssessment: window.location.pathname.startsWith('/assessments/'),
            stages: [...contestStagesRef?.current]
          }
        }
      }

      if (payload?.payload?.actionType) {
        pendingActionRef.current = payload.payload.actionType
      }

      setIsLoading(true)
      const response = await sendThreadMessage(payload)
      setIsLoading(false)

      if (response?.isError) {
        setMessages((prev) => [
          ...prev,
          {
            id: `msg-ai-${Date.now()}`,
            role: 'assistant',
            messageText: 'Sorry, something went wrong. Please try again later.',
            status: 'failed',
            messageType: 'TEXT'
          }
        ])

        return
      }

      const { threadId, messageText, messageId, status } = response

      if (!activeChatId) {
        setActiveChatId(threadId)
      }

      setMessages((prev) => [
        ...prev,
        {
          id: messageId,
          role: 'assistant',
          messageText: messageText || '',
          status,
          messageType: 'TEXT'
        }
      ])
    },
    [inputValue, paramsContestId, activeChatId]
  )

  const handleInviteClick = useCallback(
    (data) => {
      const {
        name,
        email,
        phoneNumber,
        contestId: { assessmentId: contestId, assessmentName }
      } = data

      const message = `You have confirmed the details for ${name}. The invitation for ${assessmentName} is on its way`

      const payload = {
        name,
        email,
        phoneNumber,
        actionType: CHAT_BOT_ACTION_TYPES.CANDIDATE_INVITE,
        contestId: parseInt(contestId)
      }

      handleSendMessage({ message, payload })
      setModalData({ open: false, data: null })
    },
    [handleSendMessage]
  )

  const handleCancelInvite = useCallback(() => {
    handleSendMessage({
      message: 'Cancel Invite',
      payload: { actionType: CHAT_BOT_ACTION_TYPES.CANCEL_INVITE }
    })
    setModalData({ open: false, data: null })
  }, [handleSendMessage])

  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault()
        handleSendMessage()
      }
    },
    [handleSendMessage]
  )

  const handleReEnableClick = useCallback((row) => {
    const obj = {
      contestId: row?.contestId || '',
      name: row?.name || '',
      candidateId: row?.id || null,
      stageId: row?.stageId || null,
      isAIScreening: row?.challengeType === CHALLENGE_TYPES.AI_SCREENING,
      duration: row?.stageDuration || null,
      assessmentName: row?.assessmentName?.trim()
        ? row.assessmentName.trim()
        : null
    }

    reenableDataRef.current = { ...obj }

    setOpenReEnableCandidateModal({
      open: true,
      data: { ...obj }
    })
  }, [])

  const handleConfirmClick = useCallback(
    (candidate, assessment, message) => {
      if (
        message?.actionType === CHAT_BOT_ACTION_TYPES.CANDIDATE_SUMMARY_NAVIGATE
      ) {
        navigate(
          `/assessments/${assessment?.assessmentId}/candidate-summary/${candidate?.id}`
        )
      } else if (
        message?.actionType === CHAT_BOT_ACTION_TYPES.RE_ENABLE_ATTEMPT
      ) {
        handleReEnableClick({
          name: candidate?.name,
          id: candidate?.id,
          contestId: assessment?.assessmentId,
          stageId: assessment?.stageId,
          stageDuration: assessment?.duration,
          assessmentName: assessment?.assessmentName,
          challengeType: assessment?.challengeType
        })
      } else {
        const payload = {
          message:
            message?.actionType === CHAT_BOT_ACTION_TYPES.RESEND_INVITE
              ? `Changes have been requested for resending the invitation for ${assessment?.assessmentName} to ${candidate?.name}.`
              : message?.actionType === CHAT_BOT_ACTION_TYPES.CANDIDATE_SUMMARY
                ? `Changes have been requested for generating a summary for ${assessment?.assessmentName} for ${candidate?.name}.`
                : `Changes have been requested for updating the media configuration for ${assessment?.assessmentName} for ${candidate?.name}.`,
          payload: {
            contestId: assessment?.assessmentId,
            candidateId: candidate?.id,
            actionType: message?.actionType,
            ...(message?.actionType ===
            CHAT_BOT_ACTION_TYPES.UPDATE_MEDIA_CONFIG
              ? { ...(message?.confirmation?.mediaConfig || {}) }
              : {})
          }
        }

        handleSendMessage(payload)
      }

      setSelectAssessmentModal({
        open: false,
        candidate: null,
        message: null
      })
    },
    [handleSendMessage, handleReEnableClick, navigate]
  )

  const handleOnSubmitReEnableMode = useCallback(
    async (reEnableMode = null, duration = null) => {
      const payload = {
        message: `Changes have been requested for re-enabling the attempt ${
          reenableDataRef?.current?.assessmentName
            ? `for ${reenableDataRef.current.assessmentName}`
            : ''
        } for ${reenableDataRef?.current?.name}.`,
        payload: {
          duration: duration != null ? parseInt(duration) : null,
          reEnableMode,
          contestId:
            reenableDataRef?.current?.contestId != null
              ? parseInt(reenableDataRef.current.contestId)
              : null,
          candidateId: reenableDataRef?.current?.candidateId,
          actionType: CHAT_BOT_ACTION_TYPES.RE_ENABLE_ATTEMPT
        }
      }

      handleSendMessage(payload)

      reenableDataRef.current = null
      setOpenReEnableCandidateModal({ open: false, data: null })
      setSelectAssessmentModal({
        open: false,
        candidate: null,
        message: null
      })
    },
    [handleSendMessage]
  )

  const handleInterviewScheduleModal = useCallback(
    (data = {}, message = {}) => {
      const actionType = message?.actionType || null
      setOpenInterviewScheduleModal({
        open: true,
        candidateName: data?.name || null,
        contestId: message?.confirmation?.contestId || null,
        actionType,
        inputData: {
          candidateId: data?.id || null,
          contestStageId: data?.stageId || null,
          stageName: data?.stageName || null,
          stageType: CHALLENGE_TYPES.INTERVIEW,
          scheduleId: data?.scheduleId || null,
          startTime: data?.startTime || null
        }
      })
    },
    []
  )

  const handleInterviewScheduleSubmit = useCallback(
    (data, openInterviewScheduleModal) => {
      const isReschedule =
        openInterviewScheduleModal.actionType ===
        CHAT_BOT_ACTION_TYPES.RE_SCHEDULE_INTERVIEW
      handleSendMessage({
        message: `${isReschedule ? 'Interview has been rescheduled' : 'Interview has been scheduled'} for ${openInterviewScheduleModal.candidateName || 'the candidate'}`,
        payload: {
          contestId: openInterviewScheduleModal.contestId,
          candidateId:
            openInterviewScheduleModal?.inputData?.candidateId || null,
          actionType: openInterviewScheduleModal.actionType,
          startTime: data?.startTime || null,
          contestStageId:
            openInterviewScheduleModal?.inputData?.contestStageId || null,
          interviewerIds: data?.interviewerIds || null
        }
      })
      setOpenInterviewScheduleModal({
        open: false,
        candidateName: null,
        contestId: null,
        actionType: null,
        inputData: null
      })
    },
    [handleSendMessage]
  )

  // UseEffect
  useEffect(() => {
    fabPosRef.current = fabPos
  }, [fabPos])

  useEffect(() => {
    contestStagesRef.current = [...contestStages]
  }, [contestStages])

  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 300)
    }
  }, [isOpen])

  useEffect(() => {
    if (isOpen && !activeChatId) {
      fetchThreads()
    }
  }, [isOpen, fetchThreads, activeChatId])

  useEffect(() => {
    if (lastMessage?.status === 'processing') {
      pollStatus(lastMessage.id)
    } else {
      clearInterval(pollIntervalIdRef.current)
    }

    return () => clearInterval(pollIntervalIdRef.current)
  }, [lastMessage, pollStatus])

  useEffect(() => {
    if (!activeChatId) {
      setMaxTokens(0)
      setMessages([])
      setIsLoading(false)
      setCumulativeTokens(0)
      setFetchMessages(false)
    }
  }, [activeChatId])

  useEffect(() => {
    if (messages?.length > 0) {
      setTimeout(() => {
        isUserScrolledUp.current = false
        scrollToLatestMessage('smooth', true)
      }, 100)
    }
  }, [messages?.length, scrollToLatestMessage])

  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      })

      setFabPos((prev) => {
        const boundedPosition = getBoundedFabPosition(prev.right, prev.bottom)

        fabPosRef.current = boundedPosition

        if (
          boundedPosition.right !== prev.right ||
          boundedPosition.bottom !== prev.bottom
        ) {
          return boundedPosition
        }

        return prev
      })
    }

    window.addEventListener('resize', handleResize)

    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    return () => {
      clearInterval(pollIntervalIdRef.current)

      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [])

  return (
    <>
      <Box
        className={`chatbot-fab ${isOpen ? 'chatbot-fab--active' : ''}`}
        onClick={handleFabClick}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDragStart={(e) => e.preventDefault()}
        style={{
          right: `${fabPos.right}px`,
          bottom: `${fabPos.bottom}px`,
          touchAction: 'none',
          ...(isDragging ? { transition: 'none' } : {})
        }}
        role="button"
        tabIndex={0}
        aria-label={isOpen ? 'Close chat' : 'Open chat'}
      >
        {isOpen ? (
          <Box className="chatbot-fab__close-circle">
            <IconifyIcon icon="mdi:close" fontSize="20px" />
          </Box>
        ) : (
          <img src={scoutyImg} alt="Scouty AI" />
        )}
      </Box>

      <Box
        className={`chatbot-window ${isOpen ? 'chatbot-window--open' : ''}`}
        style={{
          right: `${chatWindowRight}px`,
          bottom: `${chatWindowBottom}px`,
          ...(isDragging ? { transition: 'none' } : {})
        }}
      >
        <Box className="chatbot-window__header">
          <Box className="chatbot-window__header-left">
            <Box className="chatbot-window__header-avatar">
              <img src={scoutyImg} alt="Scouty AI" width={20} height={20} />
            </Box>
            <Box>
              <Typography
                variant="subtitle2"
                className="chatbot-window__header-title"
              >
                CoderScout AI
              </Typography>
              <Typography
                variant="caption"
                className="chatbot-window__header-status"
              >
                {lastMessage?.status === 'processing'
                  ? 'Thinking...'
                  : 'Online'}
              </Typography>
            </Box>
          </Box>

          <Box className="chatbot-window__header-actions">
            <IconifyIcon
              hoverEffect
              icon="mdi:comment-plus-outline"
              fontSize="18px"
              title="New Chat"
              onClick={handleNewChat}
            />
            <IconifyIcon
              hoverEffect
              icon="mdi:close"
              fontSize="18px"
              title="Close"
              onClick={toggleChat}
            />
          </Box>
        </Box>

        <Box className="chatbot-window__messages" onScroll={handleScroll}>
          {showHistory ? (
            <Box className="chatbot-window__welcome">
              <Box className="chatbot-window__welcome-icon">
                <img src={scoutyImg} alt="Scouty AI" width={36} height={36} />
              </Box>

              <Typography
                variant="subtitle1"
                className="chatbot-window__welcome-title"
              >
                Hi! How can I help you?
              </Typography>

              <Typography
                variant="body2"
                className="chatbot-window__welcome-subtitle"
              >
                Ask me anything about CoderScout
              </Typography>

              {isFetchingHistory ? (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mt: 3,
                    flexDirection: 'column'
                  }}
                >
                  <CircularProgress size={24} sx={{ mb: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Fetching chat history...
                  </Typography>
                </Box>
              ) : (
                <ChatHistory
                  history={chatHistory}
                  onSelectChat={handleSelectChat}
                />
              )}
            </Box>
          ) : (
            <Box className="chatbot-window__messages-list">
              {messages?.length === 0 && fetchMessages && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mt: 3,
                    flexDirection: 'column'
                  }}
                >
                  <CircularProgress size={24} sx={{ mb: 1 }} />
                  <Typography variant="caption" color="text.secondary">
                    Fetching messages...
                  </Typography>
                </Box>
              )}

              {messages.map((msg, index) => (
                <ChatMessage
                  key={msg.id}
                  message={msg}
                  isLastMessage={index === messages.length - 1}
                  isLoading={
                    msg.role === 'assistant' && msg?.status === 'processing'
                  }
                  onTyping={() => scrollToLatestMessage()}
                  onTypingStart={() => setIsBotTyping(true)}
                  onTypingEnd={() => setIsBotTyping(false)}
                  onAwaitingAprrovalClick={onAwaitingAprrovalClick}
                  onSelectCandidateClick={onSelectCandidateClick}
                  handleReEnableClick={handleReEnableClick}
                  handleInterviewClick={handleInterviewScheduleModal}
                />
              ))}

              <div ref={messagesEndRef} />
            </Box>
          )}
        </Box>

        <Box className="chatbot-window__input">
          {!!activeChatId && maxTokens > 0 && cumulativeTokens >= maxTokens && (
            <Box sx={{ width: '100%', mb: 1.5, px: 0.5 }}>
              <Typography
                variant="caption"
                color="error"
                sx={{ display: 'block', textAlign: 'center' }}
              >
                Max limit is reached, please start a new chat
              </Typography>
            </Box>
          )}

          <CustomTextField
            fullWidth
            multiline
            maxRows={4}
            value={inputValue}
            inputRef={inputRef}
            onKeyDown={handleKeyDown}
            placeholder="Type your message..."
            onChange={(e) => setInputValue(e.target.value)}
            disabled={disableTyping}
            className="chatbot-window__input-field"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={handleSendMessage}
                    disabled={!inputValue.trim() || isLoading}
                    className="chatbot-window__send-btn"
                  >
                    <IconifyIcon icon="mdi:send-outline" fontSize="20px" />
                  </IconButton>
                </InputAdornment>
              )
            }}
            sx={{ '.MuiInputBase-inputMultiline': { padding: 0 } }}
          />
        </Box>
      </Box>

      {modalData?.data?.actionType ===
        CHAT_BOT_ACTION_TYPES.CANDIDATE_INVITE && (
        <InviteCandidate
          isFromChatbot={true}
          open={modalData?.open}
          title="Invite Candidate"
          handleCancelClick={handleCancelInvite}
          handleInviteClick={handleInviteClick}
          inputData={modalData?.data?.confirmation?.data || {}}
        />
      )}

      <ResendCandidateAssessmentsModal
        open={selectAssessmentModal.open}
        onConfirmClick={handleConfirmClick}
        candidate={selectAssessmentModal?.candidate}
        message={selectAssessmentModal?.message || {}}
        onClose={() =>
          setSelectAssessmentModal({
            open: false,
            candidate: null,
            message: null
          })
        }
      />

      {openReEnableCandidateModal?.open && (
        <ReEnableAttemptModal
          open={openReEnableCandidateModal?.open}
          data={openReEnableCandidateModal?.data}
          handleClose={() =>
            setOpenReEnableCandidateModal({ open: false, data: null })
          }
          onSubmit={handleOnSubmitReEnableMode}
        />
      )}

      {openInterviewScheduleModal.open && (
        <EnableOrScheduleStageModal
          open={openInterviewScheduleModal.open}
          handleClose={() =>
            setOpenInterviewScheduleModal({
              open: false,
              candidateName: null,
              contestId: null,
              actionType: null,
              inputData: null
            })
          }
          inputData={openInterviewScheduleModal.inputData}
          stageName={openInterviewScheduleModal.inputData?.stageName}
          stageType={openInterviewScheduleModal.inputData?.stageType}
          scheduleId={openInterviewScheduleModal.inputData?.scheduleId}
          onSubmit={(data) =>
            handleInterviewScheduleSubmit(data, openInterviewScheduleModal)
          }
          title={
            openInterviewScheduleModal.actionType ===
            CHAT_BOT_ACTION_TYPES.RE_SCHEDULE_INTERVIEW
              ? `Re Schedule Interview for ${openInterviewScheduleModal.candidateName || 'Candidate'}`
              : `Schedule Interview for ${openInterviewScheduleModal.candidateName || 'Candidate'}`
          }
          scheduleBtnText={
            openInterviewScheduleModal.actionType ===
            CHAT_BOT_ACTION_TYPES.RE_SCHEDULE_INTERVIEW
              ? 'Re Schedule'
              : 'Schedule'
          }
        />
      )}
    </>
  )
}

export default Chatbot
